import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  DndContext,
  type DragEndEvent,
  type DragOverEvent,
  DragOverlay,
  type DragStartEvent,
  useSensor,
  useSensors,
  KeyboardSensor,
  Announcements,
  UniqueIdentifier,
  TouchSensor,
  MouseSensor,
} from "@dnd-kit/core";
import { SortableContext, arrayMove } from "@dnd-kit/sortable";
import { BoardColumn, BoardContainer, Column } from "./BoardColumn";
import {Task,TaskStatus} from "@/app/types/tasks";
import { hasDraggableData } from "./Utility";
import { TaskCard } from "./TaskCard";
import useTableData from "@/components/ui/table/DataTableLogic";
import {useGetAllTasksQuery,useUpdateTaskMutation} from "@/services/TasksApi";
import TableSearchFilter from "@/components/ui/table/DataTableSearch";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/MobileToolTip";
import TableFilters from "@/components/ui/table/DataTableFilter";
import { useGetStaffListQuery } from "@/services/StaffsApi";
import { DatePickerWithRange } from "@/components/ui/date-range/DateRangePicker";
import { formatDate, formatDateRange } from "@/utils/Helper";
import { InputTags } from "@/components/ui/input-tags/Tags";
import TaskDetails from "./Task Details/TaskDetails";
import { Button } from "@/components/ui/Button";
import TaskForm from "@/components/admin/tasks/component/modal/TaskForm";
import { coordinateGetter } from "./MultipleContainersPreset";
const defaultCols = [
  {
    id: TaskStatus.TODO,
    title: "Pending",
  },
  {
    id: TaskStatus.IN_PROGRESS,
    title: "In-progress",
  },
  {
    id: TaskStatus.COMPLETED,
    title: "Complete",
  },
  {
    id: TaskStatus.HOLD,
    title: "Hold",
  },
  {
    id: TaskStatus.ARCHIVED,
    title: "Archived",
  },
] satisfies Column[];

export type ColumnId = (typeof defaultCols)[number]["id"];

const initialValue = {
  limit:100,
  offset: 0,
  sort_order: "desc",
  sort_key: "id",
};
export function KanbanBoard() {
  const [isExpanded, setIsExpanded] = useState(false);
  const [taskToUpdate, setTaskToUpdate] = useState<Task | null>(null);
  const [action, setAction] = useState<"add" | "edit">("add");
  const [data, setData] = useState<Task | undefined>(undefined);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const viewkanban = (() => {
    try {
      return (
        JSON.parse(localStorage.getItem("accessLevels") as string).viewkanban ??
        "no_access"
      );
    } catch {
      return "no_access";
    }
  })();

   const taskPermission = (() => {
     try {
       return (
         JSON.parse(localStorage.getItem("accessLevels") as string)
           .viewkanban ?? "no_access"
       );
     } catch {
       return "no_access";
     }
   })();

  const [openLog, setOpenLog] = useState(false);
  const [taskId, setTaskId] = useState<number | undefined>();
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [columns, setColumns] = useState<Column[]>(defaultCols);
  const pickedUpTaskColumn = useRef<ColumnId | null>(null);
  const columnsId = useMemo(() => columns.map((col) => col.id), [columns]);
  const [inputValue, setInputValue] = useState<string>("");
  const { query, setSearchCriteria } = useTableData(initialValue);
  const { data: TaskData ,refetch} = useGetAllTasksQuery({ query });
  const [tasks, setTasks] = useState<Task[]>(TaskData?.data ?? []);
  const [openFilter, setOpenFilter] = useState(false);
  interface FilterData {
    date_from?: Date;
    date_to?: Date;
    [key: string]: any;
  }

  const [filterData, setFilterData] = useState<FilterData>({});
  const { data: staffData } = useGetStaffListQuery({ query: "" });

  useEffect(() => {
    if (TaskData) {
      setTasks(TaskData?.data);
    }
  }, [TaskData]);

  const [activeColumn, setActiveColumn] = useState<Column | null>(null);
  const [updateTaskStatus] = useUpdateTaskMutation();

  const [activeTask, setActiveTask] = useState<Task | null>(null);

  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: coordinateGetter,
    })
  );

  function getDraggingTaskData(taskId: UniqueIdentifier, columnId: ColumnId) {
    const tasksInColumn = tasks.filter((task) => task.status === columnId);
    const taskPosition = tasksInColumn.findIndex(
      (task) => task.task_id === taskId
    );
    const column = columns.find((col) => col.id === columnId);
    return {
      tasksInColumn,
      taskPosition,
      column,
    };
  }

  const announcements: Announcements = {
    onDragStart({ active }) {
      if (!hasDraggableData(active)) return;
      if (active.data.current?.type === "Column") {
        const startColumnIdx = columnsId.findIndex((id) => id === active.id);
        const startColumn = columns[startColumnIdx];
        return `Picked up Column ${startColumn?.title} at position: ${
          startColumnIdx + 1
        } of ${columnsId.length}`;
      } else if (active.data.current?.type === "Task") {
        pickedUpTaskColumn.current = active.data.current.task.status;
        const { tasksInColumn, taskPosition, column } = getDraggingTaskData(
          active.id,
          pickedUpTaskColumn.current as ColumnId
        );
        return `Picked up Task ${
          active.data.current.task.title
        } at position: ${taskPosition + 1} of ${
          tasksInColumn.length
        } in column ${column?.title}`;
      }
    },
    onDragOver({ active, over }) {
      if (!hasDraggableData(active) || !hasDraggableData(over)) return;

      if (
        active.data.current?.type === "Column" &&
        over?.data.current?.type === "Column"
      ) {
        const overColumnIdx = columnsId.findIndex((id) => id === over?.id);
        return `Column ${active.data.current.column.title} was moved over ${
          over?.data.current.column.title
        } at position ${overColumnIdx + 1} of ${columnsId.length}`;
      } else if (
        active.data.current?.type === "Task" &&
        over?.data.current?.type === "Task"
      ) {
        const { tasksInColumn, taskPosition, column } = getDraggingTaskData(
          over.id,
          over.data.current.task.status
        );
        if (over.data.current.task.status !== pickedUpTaskColumn.current) {
          return `Task ${
            active.data.current.task.title
          } was moved over column ${column?.title} in position ${
            taskPosition + 1
          } of ${tasksInColumn.length}`;
        }
        return `Task was moved over position ${taskPosition + 1} of ${
          tasksInColumn.length
        } in column ${column?.title}`;
      }
    },
    onDragEnd({ active, over }) {
      if (!hasDraggableData(active) || !hasDraggableData(over)) {
        pickedUpTaskColumn.current = null;
        return;
      }
      if (
        active.data.current?.type === "Column" &&
        over?.data.current?.type === "Column"
      ) {
        const overColumnPosition = columnsId.findIndex((id) => id === over.id);

        return `Column ${
          active.data.current.column.title
        } was dropped into position ${overColumnPosition + 1} of ${
          columnsId.length
        }`;
      } else if (
        active.data.current?.type === "Task" &&
        over?.data.current?.type === "Task"
      ) {
        const { tasksInColumn, taskPosition, column } = getDraggingTaskData(
          over.id,
          over.data.current.task.status
        );
        if (over.data.current.task.status !== pickedUpTaskColumn.current) {
          return `Task was dropped into column ${column?.title} in position ${
            taskPosition + 1
          } of ${tasksInColumn.length}`;
        }
        return `Task was dropped into position ${taskPosition + 1} of ${
          tasksInColumn.length
        } in column ${column?.title}`;
      }
      pickedUpTaskColumn.current = null;
    },
    onDragCancel({ active }) {
      pickedUpTaskColumn.current = null;
      if (!hasDraggableData(active)) return;
      return `Dragging ${active.data.current?.type} cancelled.`;
    },
  };

  const handleOpen = () => {
    setAction("add");
    setIsDialogOpen(true);
  };

  const [selectedLabels, setSelectedLabels] = useState<{
    [key: string]: string[];
  }>({});
  const handleFilterChange = (
    filterName: string,
    values: string[],
    options?: any[]
  ) => {
    setFilterData((prevFilters) => ({
      ...prevFilters,
      [filterName]: values, // Store selected values
    }));

    // Store selected labels
    const selectedOptions = options?.filter((option) =>
      values.includes(option.value)
    );
    setSelectedLabels((prevLabels) => ({
      ...prevLabels,
      [filterName]: selectedOptions
        ? selectedOptions.map((option) => option.label)
        : [],
    }));
  };
  const handleDateRange = (dates: {
    date_from: Date | undefined;
    date_to: Date | undefined;
  }) => {
    const formattedStartDate = dates.date_from
      ? formatDateRange(dates.date_from)
      : "";
    const formattedEndDate = dates.date_to
      ? formatDateRange(dates.date_to)
      : "";

    setFilterData((prev) => ({
      ...prev,
      date_from: formattedStartDate,
      date_to: formattedEndDate,
    }));

    setSearchCriteria((prev: any) => ({
      ...prev,
      date_from: formattedStartDate || undefined,
      // Only set end_date if start_date is present
      date_to:
        formattedStartDate && !formattedEndDate ? undefined : formattedEndDate,
      offset: 0,
      sort_key: "id",
      sort_order: "desc",
    }));
  };

  useEffect(() => {
    if (taskToUpdate) {
      updateTaskStatus({
        id: taskToUpdate.task_id.toString(),
        status: taskToUpdate.status,
        due_date: taskToUpdate.due_date,
        priority: taskToUpdate.priority,
        title: taskToUpdate.title,
      });
    }
  }, [taskToUpdate]); // Runs only when taskToUpdate changes
  const filterDisplay = [
    {
      type: "tags",
      name: "tags",
      label: "Tags",
      placeholder: "Add tags to filter tasks...",
      function: (tags: string[]) => {
        setFilterData((prevFilters) => ({
          ...prevFilters,
          tags: tags,
        }));

      },
    },
    {
      type: "multiselect",
      name: "assigned_to",
      label: "Assigned To",
      options: staffData?.map((item) => ({
        value: item.value,
        label: item.label,
      })),
      function: (value: string[]) => handleFilterChange("assigned_to", value),
    },
    {
      type: "multiselect",
      name: "priority",
      label: "Priority",
      options: [
        { value: "low", label: "Low" },
        { value: "medium", label: "Medium" },
        { value: "high", label: "High" },
      ],
      function: (value: string[]) => handleFilterChange("priority", value),
    },
  ];

  const handleOpenEdit = (task: Task) => {
    setAction("edit");
    setData(task);
    setIsDialogOpen(true);
  };

  return (
    <DndContext
      accessibility={{
        announcements,
      }}
      sensors={sensors}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
    >
      <div className="px-3 py-2  flex flex-row justify-between items-center">
        <div>
          {/* Table Filter component */}
          <TableSearchFilter
            inputValue={inputValue}
            setInputValue={setInputValue}
            setSearchCriteria={setSearchCriteria}
            placeHolder={"Search"}
          />
        </div>

        <div className="flex flex-row justify-between items-center px-3 py-2 gap-3">
          <div className="flex flex-row gap-2 items-center justify-center">
            <div>
              <Button
                access={taskPermission !== "read"}
                className="bg-primary text-sm text-nowrap  text-white flex items-center gap-1  lg:mb-0 h-8 px-2"
                onClick={handleOpen}
              >
                <i className="fa-solid fa-plus" />
                {""}
                Create New
              </Button>
            </div>
            {/* Date Picker */}
            <div className="text-sm text-black flex items-center w-fit justify-center gap-1 h-8 md:text-xs  md:w-auto ">
              <DatePickerWithRange
                name="Date Range"
                value={{
                  date_From: filterData.date_from,
                  date_to: filterData.date_to,
                }}
                onValueChange={handleDateRange}
                // label="Select date"
                className="w-full md:w-auto"
              />
            </div>
            {/* Reset Button */}
            <button
              className="text-xs md:text-sm border border-primary rounded-lg  flex items-center gap-1 h-8 px-2"
              onClick={() => {
                setFilterData({});
                setSearchCriteria(initialValue);
                setInputValue("");
                setSelectedTags([]); // Clear tags
              }}
            >
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex gap-1 justify-center items-center">
                      <i className="fa-solid fa-arrows-rotate text-gray-400"></i>
                      {/* <span className="hidden md:inline">Reset</span> */}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Reset page</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </button>
          </div>
          <button
            className={`border rounded-full size-3 text-gray-400 p-4 flex items-center justify-center ${Object.keys(filterData).length ? "border-primary" : ""}`}
            onClick={() => setOpenFilter(true)}
          >
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <i
                    className={`fa fa-filter ${Object.keys(filterData).length ? "text-primary" : ""}`}
                  ></i>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <p className="capitalize">click to apply filter</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </button>
        </div>
      </div>

      {/* Toggle Button */}
      {isExpanded && (
        <>
          <div className="flex flex-row  lg:items-center justify-between gap-4 px-3 pb-2 ">
            {/* Buttons Container */}
            <div className="flex flex-row  justify-end lg:flex-row lg:justify-between lg:items-center gap-2 w-full">
              <div className="w-[50%]">
                <InputTags
                  value={selectedTags}
                  onChange={(tags) => {
                    setSelectedTags(tags);
                    setSearchCriteria((prev) => ({
                      ...prev,
                      tags: tags, // Update query with tags
                      offset: 0, // Reset pagination
                    }));
                  }}
                />
              </div>
            </div>
          </div>
        </>
      )}

      <BoardContainer>
        <SortableContext items={columnsId}>
          {columns.map((col) => (
            <BoardColumn
              key={col.id}
              column={col}
              tasks={tasks.filter((task) => task.status === col.id)}
              isDraggable={viewkanban === "write"} // Pass draggable state
              setOpenLog={setOpenLog}
              setTaskId={setTaskId}
              viewKanban={viewkanban}
              onEditTask={handleOpenEdit}
            />
          ))}
        </SortableContext>
      </BoardContainer>

      {"document" in window &&
        createPortal(
          <DragOverlay>
            {activeColumn && (
              <BoardColumn
                isOverlay
                column={activeColumn}
                tasks={tasks.filter((task) => task.status === activeColumn.id)}
                setOpenLog={setOpenLog}
                setTaskId={setTaskId}
                viewKanban={viewkanban}
                onEditTask={handleOpenEdit} // Pass the edit function to overlay as well
              />
            )}
            {activeTask && (
              <TaskCard
                task={activeTask}
                isOverlay
                setOpenLog={setOpenLog}
                setTaskId={setTaskId}
                viewKanban={viewkanban}
                onEditTask={handleOpenEdit} // Pass the edit function to overlay as well
              />
            )}
          </DragOverlay>,
          document.body
        )}
      {/* table filters */}
      <TableFilters
        isOpen={openFilter}
        setOpen={setOpenFilter}
        initialValue={initialValue}
        filterData={filterData}
        setFilter={setFilterData}
        setSearchCriteria={setSearchCriteria}
        filterDisplay={filterDisplay}
        setInputValue={setInputValue}
      />
      <TaskDetails
        isOpen={openLog}
        setOpen={setOpenLog}
        ticketId={taskId}
        access={viewkanban}
      />
      {/* form */}
      <TaskForm
        isOpen={isDialogOpen}
        setOpen={setIsDialogOpen}
        action={action}
        setAction={setAction}
        data={data}
        setData={setData}
        refetch={refetch}
        isgettingCreate={false}
      />
    </DndContext>
  );

  function onDragStart(event: DragStartEvent) {
    if (!hasDraggableData(event.active)) return;
    const data = event.active.data.current;
    if (data?.type === "Column" && viewkanban !== "write") return;

    if (data?.type === "Column") {
      setActiveColumn(data.column);
      return;
    }

    if (data?.type === "Task") {
      setActiveTask(data.task);
      return;
    }
  }

  function onDragOver(event: DragOverEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    if (!hasDraggableData(active) || !hasDraggableData(over)) return;

    const activeData = active.data.current;
    const overData = over.data.current;

    const isActiveATask = activeData?.type === "Task";
    const isOverATask = overData?.type === "Task";

    // Handle task reordering within the same column
    if (isActiveATask && isOverATask) {
      setTasks((tasks) => {
        const activeIndex = tasks.findIndex((t) => t.task_id === active.id);
        const overIndex = tasks.findIndex((t) => t.task_id === over.id);

        if (activeIndex === -1 || overIndex === -1) return tasks;

        return arrayMove(tasks, activeIndex, overIndex);
      });
    }
  }

  function onDragEnd(event: DragEndEvent) {
    setActiveColumn(null);
    setActiveTask(null);

    const { active, over } = event;
    if (!over || active.id === over.id) return;

    if (!hasDraggableData(active) || !hasDraggableData(over)) return;

    const activeData = active.data.current;
    const overData = over.data.current;

    const isActiveATask = activeData?.type === "Task";
    const isOverAColumn = overData?.type === "Column";
    const isOverATask = overData?.type === "Task";

    // Handle when a task is dragged onto a column
    if (isActiveATask && isOverAColumn) {
      const finalStatus = over.id as ColumnId;

      setTasks((tasks) => {
        const activeIndex = tasks.findIndex((t) => t.task_id === active.id);
        if (activeIndex === -1) return tasks;

        const updatedTasks = tasks.map((task, index) =>
          index === activeIndex ? { ...task, status: finalStatus } : task
        );

        // Store task separately to trigger API call only once
        setTaskToUpdate(updatedTasks[activeIndex]);

        return updatedTasks;
      });
    }
    // Handle when a task is dragged onto another task (potentially in a different column)
    else if (isActiveATask && isOverATask) {
      const activeTask = tasks.find((t) => t.task_id === active.id);
      const overTask = tasks.find((t) => t.task_id === over.id);

      if (!activeTask || !overTask) return;

      // If tasks are in different columns, change the column of the active task
      if (activeTask.status !== overTask.status) {
        setTasks((tasks) => {
          const activeIndex = tasks.findIndex((t) => t.task_id === active.id);
          if (activeIndex === -1) return tasks;

          const updatedTasks = tasks.map((task, index) =>
            index === activeIndex ? { ...task, status: overTask.status } : task
          );

          // Store task separately to trigger API call only once
          setTaskToUpdate(updatedTasks[activeIndex]);

          return updatedTasks;
        });
      }
      // If in the same column, just reorder (which is already handled in onDragOver)
    }
  }
}